import { giphyBlack, giphyRed } from '@giphy/js-brand'
import { useContext, useRef, useState } from 'react'
import MessageContext from 'shared/contexts/message'
import UserContext from 'shared/contexts/user'
import useAsyncEffect from 'shared/hooks/use-async-effect'
import styled from 'styled-components'
import { SUPER_CUT_MAX_FILE_DURATION, SUPER_CUT_MIN_FILE_DURATION } from '../../../constants'
import { CreationToolsContext } from '../../../manager'
import { CreationToolSourceFile } from '../../../types'
import CreationTool from '../../../types/creation-tool'
import CreationToolError from '../../../types/error'
import CreationToolWatermarkPosition from '../../../types/watermark'
import getDefaultSourceTitle from '../../../utils/get-default-source-title'
import validateVideo from '../../../utils/validate-video'
import AlertModal from '../../alert-modal'
import { ColumnBlock, ContainerBlock, SplitBlock } from '../../blocks'
import Banner from '../../modal-banner'
import Fields from '../shared/components/capture-info'
import InfoPills from '../shared/components/capture-info-pills'
import CapturePreview from '../shared/components/capture-preview'
import { INewCaptureInfo } from '../shared/types'
import API from '../shared/utils/api'
import BaseModal from './base'
import { SourceEditorCaptionStyle } from '../../source-editor/types'
import { ArtboardDragPosition } from '../../artboard/types'
import SuperCutSegmentationType from '../../../types/super-cut-segmentation-type'

const api = new API()

const Container = styled(ContainerBlock)`
    padding-top: 50px;
    position: relative;
    width: 1040px;
`

const Wrapper = styled(SplitBlock)`
    background: ${giphyBlack};
    border-top-left-radius: 0;
    border-top-right-radius: 0;
`

const Preview = styled(CapturePreview)`
    padding-top: 30px;
`

type Props = {
    source: File | string
    onComplete?: (uuid: string) => void
    onClose: () => void
}

const CreationToolsNewCaptureModal = ({ source, onComplete, onClose }: Props) => {
    const { tool, trackEvent } = useContext(CreationToolsContext)
    const { sendMessage } = useContext(MessageContext)
    const { user } = useContext(UserContext)
    const [isAutoCaptionOn, setIsAutoCaptionOn] = useState<boolean>(false)
    const [error, setError] = useState<CreationToolError>()
    const [validatedSource, setValidatedSource] = useState<CreationToolSourceFile>()
    const [videoPlayerWidth, setVideoPlayerWidth] = useState<number>(460)
    const [hasChanges, setHasChanges] = useState<boolean>(false)
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isUploading, setIsUploading] = useState<boolean>(false)
    const [uploadProgress, setUploadProgress] = useState<number>(0)
    const [watermark, setWatermark] = useState<string | undefined>()
    const [watermarkPosition, setWatermarkPosition] = useState<CreationToolWatermarkPosition | undefined>()
    const watermarkWidthRef = useRef<number>()
    const captionPosition = useRef<ArtboardDragPosition>()
    const [captionStyle, setCaptionStyle] = useState<SourceEditorCaptionStyle | undefined>()
    const [hasCaptions, setHasCaptions] = useState<boolean | undefined>(false)

    useAsyncEffect(async () => {
        try {
            setIsLoading(true)
            const validated = await validateVideo(
                source,
                SUPER_CUT_MIN_FILE_DURATION,
                SUPER_CUT_MAX_FILE_DURATION,
                false,
                true,
                'dvr'
            )
            const aspectRatio = validated.width / validated.height
            setHasCaptions(validated.hasCaptions)
            setVideoPlayerWidth(Math.min(460, 500 * aspectRatio))
            setValidatedSource(validated)
            setIsLoading(false)
        } catch (e) {
            setIsLoading(false)
            setError(e)
        }
    }, [source])

    const onWatermarkResize = (width: number) => {
        watermarkWidthRef.current = width
    }

    const onCaptionMove = (pos: ArtboardDragPosition) => {
        if (!validatedSource) return
        const scale = validatedSource.width / videoPlayerWidth
        captionPosition.current = {
            ...pos,
            x: Math.round(pos.x * scale),
            y: Math.round(pos.y * scale),
        }
    }

    const onUpdate = (hasChanges: boolean, info: INewCaptureInfo) => {
        setHasChanges(hasChanges)
        setWatermark(info.watermark?.image)
        setWatermarkPosition(info.watermarkPosition)
        setCaptionStyle(info.captionStyle)
        setIsAutoCaptionOn(info.autoCaption || false)
    }

    const onSave = async (saveInfo: INewCaptureInfo) => {
        const info = {
            ...saveInfo,
            watermarkWidth: watermarkWidthRef.current,
            captionPosition: captionPosition.current,
        }

        setIsUploading(true)

        if (saveInfo.segmentationType === SuperCutSegmentationType.Caption && saveInfo.sourceUrl) {
            const language = saveInfo.language ? saveInfo.language : ''
            const captionResponse = await api.downloadYoutubeCaption(saveInfo.sourceUrl, language)
            if (!captionResponse.success) {
                setIsUploading(false)
                setError(CreationToolError.CaptionDownloadFailed)
                return
            }
            info.captionFilename = captionResponse.filename // TODO: Use this in DVR eventually and remove line below
            info.language = captionResponse.filename.split('.')[1]
        }

        try {
            if (typeof source === 'string') {
                setUploadProgress(1)
            }

            const { uuid } =
                typeof source === 'string'
                    ? await api.newCaptureFromUrl(info, source)
                    : await api.newCaptureFromFile(info, source, setUploadProgress)

            const gaCategory = CreationTool.gaEventCategory(tool)

            // watermark ga event
            if (saveInfo.watermark) {
                trackEvent(gaCategory, 'Watermark', '')
            }

            // font ga event
            if (saveInfo.font) {
                trackEvent(gaCategory, 'Font', '')
            }

            if (uuid) {
                // delay the complete handler a bit to prevent things
                // from feeling hectic when it finishes.
                setTimeout(() => {
                    sendMessage('Upload Complete!', 'success')
                    setIsUploading(false)
                    onComplete && onComplete(uuid)
                }, 2000)
            } else {
                setIsUploading(false)
                setError(CreationToolError.SuperCutUploadFailed)
            }
        } catch (err) {
            setIsUploading(false)
            setError(CreationToolError.SuperCutUploadFailed)
        }
    }

    const onCloseClick = () => {
        if (isUploading) {
            return
        }

        if (hasChanges && !error) {
            setShowConfirmModal(true)
            return
        }

        onClose()
    }

    return (
        <BaseModal
            {...{
                error,
                isLoading,
                onClose: onCloseClick,
            }}
        >
            <Container style={showConfirmModal ? { display: 'none' } : {}}>
                <Banner>
                    <i className="ss-alert" /> Please only upload video that you own. Upgraded channels hosting
                    unrelated content can be downgraded.
                </Banner>
                <Wrapper>
                    <ColumnBlock>
                        {validatedSource && (
                            <>
                                <Preview
                                    {...{
                                        source: validatedSource,
                                        width: videoPlayerWidth,
                                        watermark,
                                        watermarkPosition,
                                        onWatermarkResize,
                                        captionStyle,
                                        onCaptionMove: onCaptionMove,
                                        autoCaption: isAutoCaptionOn,
                                    }}
                                />
                                <InfoPills
                                    {...{
                                        size: typeof source === 'string' ? 0 : source.size / (1024 * 1024),
                                        duration: validatedSource.duration,
                                        captureCount: 0,
                                    }}
                                />
                            </>
                        )}
                    </ColumnBlock>
                    <ColumnBlock>
                        {validatedSource && (
                            <Fields
                                {...{
                                    uploadProgress,
                                    isUploading,
                                    onSave,
                                    captureInfo: {
                                        title: getDefaultSourceTitle(validatedSource),
                                        username: user?.username,
                                        sourceUrl: typeof source === 'string' ? source : '',
                                        hasCaptions: hasCaptions,
                                        // if we only have autoCaptions, don't default to caption-based segmentation
                                        segmentationType: SuperCutSegmentationType.Scene,
                                        languages: validatedSource.languages,
                                    },
                                    saveLabel: 'Start Capture',
                                    onUpdate: onUpdate,
                                    onCancel: onCloseClick,
                                }}
                            />
                        )}
                    </ColumnBlock>
                </Wrapper>
            </Container>
            {showConfirmModal && (
                <AlertModal
                    {...{
                        stickerId: 'jOsoGmmWGSloPU8fMH',
                        title: 'You will lose your changes to this capture!',
                        primaryButtonLabel: 'Discard Changes',
                        primaryButtonColor: giphyRed,
                        secondaryButtonLabel: 'Nevermind',
                        onPrimaryButtonClick: onClose,
                        onSecondaryButtonClick: () => setShowConfirmModal(false),
                    }}
                />
            )}
        </BaseModal>
    )
}

export default CreationToolsNewCaptureModal
